import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { FiPlus, FiX } from "react-icons/fi";

/**
 * Mobile-only speed dial. Portaled to body so page overflow cannot hide it.
 * Hidden from md and up. Pass actions with { id, label, icon, onClick, show? }.
 * One visible action: tap opens it. Multiple: tap expands the menu.
 */
const MobileCreateFab = ({
  actions = [],
  ariaLabel = "Create",
  className = "",
}) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [alertsVisible, setAlertsVisible] = useState(false);
  const visible = actions.filter((action) => action && action.show !== false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const onAlertsVisibility = (event) => {
      setAlertsVisible(Boolean(event?.detail?.visible));
    };
    setAlertsVisible(Boolean(window.__crmAlertsFabVisible));
    window.addEventListener("crm:alerts-fab-visible", onAlertsVisibility);
    return () =>
      window.removeEventListener("crm:alerts-fab-visible", onAlertsVisibility);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!mounted || !visible.length) return null;

  const runAction = (action) => {
    setOpen(false);
    action?.onClick?.();
  };

  const handleMainClick = () => {
    if (visible.length === 1) {
      runAction(visible[0]);
      return;
    }
    setOpen((prev) => !prev);
  };

  // Sit in the alerts slot when the bell is hidden; otherwise stack above it.
  const onCallsDesk = pathname.startsWith("/my-calls");
  const positionClass = onCallsDesk
    ? alertsVisible
      ? "bottom-[calc(9.5rem+env(safe-area-inset-bottom))] right-4"
      : "bottom-[calc(4.25rem+env(safe-area-inset-bottom))] right-4"
    : alertsVisible
      ? "bottom-48 right-8"
      : "bottom-28 right-8";

  return createPortal(
    <div
      className={`fixed ${positionClass} z-[80] md:hidden transition-[bottom] duration-200 ${className}`}
    >
      {open && (
        <button
          type="button"
          aria-label="Close create menu"
          className="fixed inset-0 z-[79] bg-black/20 backdrop-blur-[1px]"
          onClick={() => setOpen(false)}
        />
      )}

      <div className="relative z-[80] flex flex-col-reverse items-end gap-3">
        <button
          type="button"
          onClick={handleMainClick}
          aria-label={open ? "Close create menu" : ariaLabel}
          aria-expanded={visible.length > 1 ? open : undefined}
          className={`flex h-12 w-12 items-center justify-center rounded-full bg-[#3f8cff] text-white shadow-xl transition-all duration-200 hover:bg-[#2f6bff] active:scale-95 ${
            open ? "rotate-45" : ""
          }`}
        >
          {open ? <FiX size={22} /> : <FiPlus size={22} />}
        </button>

        {open &&
          visible.map((action) => (
            <button
              key={action.id || action.label}
              type="button"
              onClick={() => runAction(action)}
              className="flex items-center gap-2 rounded-full bg-white pl-2.5 pr-4 py-2 text-sm font-semibold text-slate-700 shadow-lg border border-slate-100"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F4F9FD] text-[#3F8CFF]">
                {action.icon}
              </span>
              {action.label}
            </button>
          ))}
      </div>
    </div>,
    document.body
  );
};

export default MobileCreateFab;
